import { ChevronLeft } from "lucide-react";
import React, { ReactNode, useEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setFilterWidth } from "../store/state/FilterWidth";

const NewFilterContainer = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(true);
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const handleWidthChange = useMemo(
    () => (width: number) => {
      if (open) {
        const roundedWidth = Math.ceil(width + 30);
        dispatch(setFilterWidth(`calc(100% - ${roundedWidth}px - 0px)`));
      }
    },
    [dispatch, open],
  );

  const debouncedHandleWidthChange = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<typeof handleWidthChange>) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleWidthChange(...args);
        timeoutId = null;
      }, 100);
    };
  }, [handleWidthChange]);

  useEffect(() => {
    if (!containerRef.current) return;
    const currentContainer = containerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        debouncedHandleWidthChange(entry.contentRect.width);
      }
    });
    resizeObserver.observe(currentContainer);
    return () => resizeObserver.unobserve(currentContainer);
  }, [debouncedHandleWidthChange]);

  useEffect(() => {
    if (!open) {
      dispatch(setFilterWidth(`calc(100% - 35px)`));
    }
  }, [open, dispatch]);

  return (
    <div
      className="hide-btn-outer relative flex"
      style={{ overflow: "visible" }}
    >
      {/* Sidebar panel — no inner scroll; hide-btn-outer handles scrolling */}
      <div
        ref={containerRef}
        className="relative shrink-0 transition-all duration-300 ease-in-out"
        style={{
          width: open ? "172px" : "0px",
          opacity: open ? 1 : 0,
          overflow: open ? "visible" : "hidden",
        }}
      >
        <div
          className="bg-white rounded-2xl m-[0.8rem] me-1"
          style={{ height: "calc(100vh - 95px)", overflowY: "auto" }}
        >
          {children}
        </div>
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="hide-btn flex items-center justify-center gap-1 transition-all duration-300 ease-in-out text-nowrap"
        style={{
          position: "absolute",
          zIndex: 99,
          top: "50%",
          transform: "translateY(-50%)",
          right: open ? "-14px" : undefined,
          left: open ? undefined : "4px",
          width: hovered ? "100px" : "26px",
          height: "26px",
          border: "1px solid #F1935C",
          borderRadius: "50px",
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 8px rgba(241,147,92,0.18)",
          padding: "0 5px",
          overflow: "hidden",
        }}
        aria-label={open ? "Hide Filters" : "Show Filters"}
      >
        <ChevronLeft
          size={14}
          className="shrink-0 text-[#F1935C] transition-transform duration-300"
          style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)" }}
        />
        {hovered && (
          <span className="text-[#F1935C] text-[0.6rem] font-medium leading-none flex items-center">
            {open ? "Hide Filters" : "Show Filters"}
          </span>
        )}
      </button>
    </div>
  );
};

export default NewFilterContainer;
